import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
import { loadConfig } from '@/configLoader'
import { showBanner } from '@/services/ui/banner'
import { notify } from '@/services/localNotify'

// Asks the store whether a newer version is out: Google Play's in-app update check on
// Android, Apple's public App Store lookup on iOS. Both are free and need no server.
// Checked when the app starts or comes back to the front, at most every 6 hours. A found
// update shows a banner (again a day after "Later") and one system notification per version.

const CHECK_EVERY_MS = 6 * 60 * 60 * 1000
const SNOOZE_MS = 24 * 60 * 60 * 1000
const STATE_KEY = 'appUpdate.state'

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STATE_KEY)) || {}
  } catch {
    return {}
  }
}
const writeState = state => {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state))
  } catch {
    // Without storage the app just checks again next time.
  }
}

// '3.0.10' > '3.0.9'
export const isNewerVersion = (candidate, current) => {
  const a = String(candidate).split('.').map(Number)
  const b = String(current).split('.').map(Number)
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0)
  }
  return false
}

const isAndroid = () => Capacitor.getPlatform() === 'android'

// null when the installed version is the newest.
const askStore = async () => {
  const info = await AppUpdate.getAppUpdateInfo({ country: 'DE' })
  if (isAndroid()) {
    return info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE
      ? { version: String(info.availableVersionCode), immediate: Boolean(info.immediateUpdateAllowed) }
      : null
  }
  return info.availableVersionName && isNewerVersion(info.availableVersionName, info.currentVersionName)
    ? { version: info.availableVersionName }
    : null
}

const openUpdate = async update => {
  try {
    // Android: Google Play's own update screen inside the app when Play allows it.
    if (isAndroid() && update.immediate) return await AppUpdate.performImmediateUpdate()
    if (isAndroid()) return await AppUpdate.openAppStore()
    const [config, { id }] = await Promise.all([loadConfig(), App.getInfo()])
    await AppUpdate.openAppStore({ appId: config.appStoreIds?.[id] ?? config.appStoreId })
  } catch (err) {
    console.warn('[Update] Could not open the store:', err)
  }
}

let checking = false

export async function checkForAppUpdate({ force = false } = {}) {
  if (!Capacitor.isNativePlatform() || checking) return
  checking = true
  try {
    const { build, version } = await App.getInfo()
    const installed = isAndroid() ? build : version
    let state = readState()

    // A cached result is only valid for the version that was installed when it was found.
    if (force || state.installed !== installed || Date.now() - (state.checkedAt || 0) >= CHECK_EVERY_MS) {
      const update = await askStore()
      state = { ...state, installed, checkedAt: Date.now(), update }
      writeState(state)
    }

    const update = state.update
    if (!update) return

    if (state.notifiedVersion !== update.version) {
      writeState({ ...state, notifiedVersion: update.version })
      notify({ title: 'Update available', body: 'A new version of the Hammerbrooklyn app is ready. Update to keep your doors working smoothly.', banner: false })
    }

    if (state.snoozedVersion === update.version && Date.now() < (state.snoozedUntil || 0)) return

    showBanner({
      title: 'New version available',
      message: 'Please update the app to get the latest fixes.',
      icon: 'mdi-download-circle-outline',
      duration: 0,
      action: { label: 'Update', onClick: () => openUpdate(update) },
      dismissLabel: 'Later',
      onDismiss: () => writeState({ ...readState(), snoozedVersion: update.version, snoozedUntil: Date.now() + SNOOZE_MS }),
    })
  } catch (err) {
    // Builds not installed from the store (debug, sideloaded) have no update info.
    console.warn('[Update] Check skipped:', err?.message || err)
  } finally {
    checking = false
  }
}

let started = false

export function startAppUpdateChecks() {
  if (started || !Capacitor.isNativePlatform()) return
  started = true
  checkForAppUpdate()
  App.addListener('resume', () => checkForAppUpdate())
}
